import { Pencil, Printer } from 'lucide-react'
import { Link, useParams, useSearchParams } from 'react-router'
import { Choice } from '@/components/Choice'
import { FormError } from '@/components/FormError'
import { RecipeSheet } from '@/components/RecipeSheet'
import { Button } from '@/components/ui/button'
import { usePageFrame } from '@/lib/frame'
import { sheetText, type SheetStyle } from '@/lib/recipeSheet'
import { cardPhotos, useRecipe, type Recipe } from '@/lib/recipes'

export function RecipePrint() {
  const { id = '' } = useParams()
  const recipe = useRecipe(id)
  if (recipe.isPending) return null
  if (recipe.isError) return <FormError error={recipe.error} />
  return <PrintView recipe={recipe.data} />
}

// The recipe set out ready to print, as a full page or a note card. The choices live in the address, so a link to
// the card version opens the card version.
function PrintView({ recipe }: { recipe: Recipe }) {
  const [params, setParams] = useSearchParams()
  const style: SheetStyle = params.get('style') === 'card' ? 'card' : 'page'
  const original = params.get('original') !== 'off' && cardPhotos(recipe).length > 0
  const set = (key: string, value: string | null) =>
    setParams(
      (p) => {
        if (value === null) p.delete(key)
        else p.set(key, value)
        return p
      },
      { replace: true },
    )
  usePageFrame(recipe.familyId, {
    title: 'Print',
    back: { to: `/recipe/${recipe.id}`, label: `Back to ${recipe.title}` },
  })
  const { ingredients, steps, notes } = sheetText(recipe)
  const typed = ingredients.length > 0 || steps.length > 0 || !!notes

  return (
    <article className="flex flex-col gap-8 pt-4 nav:pt-12">
      <header className="flex flex-col gap-2.5">
        <Link to={`/recipe/${recipe.id}`} className="self-start eyebrow hover:underline max-nav:hidden">
          {recipe.title}
        </Link>
        <h1 className="text-display">Ready to print</h1>
      </header>

      <div className="flex flex-col gap-6">
        <Choice
          legend="Style"
          hint={style === 'page' ? 'A full page, 8½ × 11 inches.' : 'A note card, 6 × 4 inches.'}
          value={style}
          options={[
            ['page', 'Full page'],
            ['card', 'Note card'],
          ]}
          onChange={(v) => set('style', v === 'page' ? null : v)}
        />
        {style === 'page' && cardPhotos(recipe).length > 0 && (
          <Choice
            legend="The original card"
            value={original ? 'on' : 'off'}
            options={[
              ['on', 'Show it'],
              ['off', 'Leave it off'],
            ]}
            onChange={(v) => set('original', v === 'on' ? null : v)}
          />
        )}
        <Button size="lg" className="self-start" onClick={() => window.print()}>
          <Printer aria-hidden /> Print
        </Button>
      </div>

      {!typed && (
        <p className="rounded-2xl border border-dashed p-4 text-sm text-muted-foreground">
          Only the photo of this recipe is kept so far. Type in its ingredients and steps to see them set out here.
          {recipe.mine && (
            <>
              {' '}
              <Link
                to={`/recipe/${recipe.id}/edit`}
                className="inline-flex items-center gap-1 text-brand underline underline-offset-4"
              >
                <Pencil className="size-3.5" aria-hidden /> Edit recipe
              </Link>
            </>
          )}
        </p>
      )}

      <div className="-mx-5 rounded-[2rem] table-surface p-4 sm:mx-0 sm:p-8">
        <div className="mx-auto flex max-w-[42rem] flex-col gap-6 [&_.sheet]:shadow-paper">
          <RecipeSheet recipe={recipe} style={style} original={original} />
        </div>
      </div>
    </article>
  )
}
